import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react'

import { getCurrentBeneficiary } from '@/store/helpers'
import { CommandInput } from '@repo/ui/command'
import { useAsyncData } from '@repo/ui/dataProvider'
import { useT } from '@repo/ui/hooks'
import { debounce } from 'lodash'

export interface SearchInputRef {
  isInputEmpty: () => boolean
}

export const SearchInput = forwardRef<
  SearchInputRef,
  { open: boolean; query: string; setQuery: (query: string) => void; onClick: () => void }
>(function ({ open, query, setQuery, onClick }, ref) {
  const { t } = useT({ keyPrefix: 'organization' })
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (open) {
      handleFocus()
    } else {
      setQuery('')
      handleBlur()
    }
  }, [open])

  const handleFocus = useCallback(
    debounce(() => {
      // Only focus if not already focused
      if (document.activeElement !== inputRef.current) inputRef.current?.focus()
    }, 100),
    [],
  )

  const handleBlur = useCallback(
    debounce(() => {
      // Only blur if not already blured
      if (document.activeElement === inputRef.current) inputRef.current?.blur()
    }, 100),
    [],
  )

  // Expose to parent
  useImperativeHandle(
    ref,
    (): SearchInputRef => ({
      isInputEmpty: () => query.trim() === '',
    }),
  )

  const {
    data: [beneficiary],
  } = useAsyncData([getCurrentBeneficiary])

  const placeholder = beneficiary?.fullName ?? t('searchBeneficiary')
  return (
    <CommandInput
      className="border-primary-200 rounded-md border px-0"
      hasCommandShortcut
      iconClassName="ml-3"
      inputClassName="text-base h-9 py-5"
      onValueChange={setQuery}
      placeholder={placeholder}
      ref={inputRef}
      value={query}
      onClick={onClick}
    />
  )
})
